import React, { useState } from "react";
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { PhoneCall, Users, MessageSquare, CalendarCheck, TrendingUp, ArrowUpRight, Info, Euro, GripVertical, Clock } from "lucide-react";
import { useI18n } from "../../i18n/I18nContext";

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

// Example (non-personal) demo data — neutral placeholder labels only
const revenueData = Array.from({ length: 14 }, (_, i) => ({
  d: `${i + 1}`,
  rev: 800 + Math.round(Math.sin(i * 0.6) * 400 + i * 90 + 300),
}));
const bookedByDay = [4, 7, 5, 9, 12, 6, 3];
const recoveredByDay = [6, 9, 8, 11, 14, 9, 5];

const exampleCards = {
  new: [
    { id: "e1", value: 180 },
    { id: "e2", value: 220 },
  ],
  contacted: [
    { id: "e3", value: 140 },
    { id: "e4", value: 420 },
  ],
  qualified: [
    { id: "e5", value: 350 },
  ],
  booked: [
    { id: "e6", value: 220 },
    { id: "e7", value: 380 },
  ],
  won: [
    { id: "e8", value: 680 },
  ],
  lost: [
    { id: "e9", value: 0 },
  ],
};

const stageColors = {
  new: "border-blue-400/30 text-blue-300",
  contacted: "border-yellow-400/30 text-yellow-300",
  qualified: "border-purple-400/30 text-purple-300",
  booked: "border-cyan-400/30 text-cyan-300",
  won: "border-emerald-400/30 text-emerald-300",
  lost: "border-slate-400/30 text-slate-400",
};

export const LiveDashboard = () => {
  const { t } = useI18n();
  const [board, setBoard] = useState(exampleCards);
  const [dragging, setDragging] = useState(null);

  const weeklyData = t.liveDash.days.map((day, i) => ({ day, booked: bookedByDay[i], recovered: recoveredByDay[i] }));

  const stages = [
    { key: "new", label: t.kanban.stages.new },
    { key: "contacted", label: t.kanban.stages.contacted },
    { key: "qualified", label: t.kanban.stages.qualified },
    { key: "booked", label: t.kanban.stages.booked },
    { key: "won", label: t.kanban.stages.won },
    { key: "lost", label: t.kanban.stages.lost },
  ];

  const metrics = [
    { icon: PhoneCall, label: t.dash.missed, value: "147", trend: "+22%", tone: "blue" },
    { icon: Users, label: t.dash.leads, value: "112", trend: "+31%", tone: "cyan" },
    { icon: MessageSquare, label: t.dash.conv, value: "284", trend: "+18%", tone: "purple" },
    { icon: CalendarCheck, label: t.dash.apps, value: "73", trend: "+27%", tone: "green" },
    { icon: TrendingUp, label: t.dash.revenue, value: "€18,420", trend: "+34%", tone: "amber" },
  ];

  const colorMap = {
    blue: "text-blue-300 bg-blue-500/10",
    cyan: "text-cyan-300 bg-cyan-500/10",
    purple: "text-purple-300 bg-purple-500/10",
    green: "text-emerald-300 bg-emerald-500/10",
    amber: "text-amber-300 bg-amber-500/10",
  };

  const onDragStart = (cardId, fromStage) => setDragging({ cardId, fromStage });
  const onDrop = (toStage) => {
    if (!dragging || dragging.fromStage === toStage) { setDragging(null); return; }
    const card = board[dragging.fromStage].find((c) => c.id === dragging.cardId);
    if (!card) return;
    setBoard((b) => ({
      ...b,
      [dragging.fromStage]: b[dragging.fromStage].filter((c) => c.id !== dragging.cardId),
      [toStage]: [card, ...b[toStage]],
    }));
    setDragging(null);
  };

  return (
    <section className="py-12" data-testid="live-dashboard">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Demo disclaimer */}
        <div className="mb-6 rounded-2xl border border-amber-400/30 bg-amber-500/10 px-5 py-4 flex items-start gap-3" data-testid="demo-disclaimer">
          <div className="w-8 h-8 rounded-lg bg-amber-500/20 border border-amber-400/40 text-amber-200 flex items-center justify-center shrink-0">
            <Info size={16} />
          </div>
          <div className="flex-1">
            <div className="text-[10px] tracking-widest font-bold text-amber-200 mb-1">{t.liveDash.disclaimerTag}</div>
            <p className="text-slate-200 text-sm leading-relaxed">{t.dash.disclaimer}</p>
          </div>
        </div>

        {/* Metric cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6" data-testid="dashboard-metrics">
          {metrics.map((m, i) => {
            const Icon = m.icon;
            return (
              <div key={m.label} className="glass rounded-xl p-5" data-testid={`metric-${i}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colorMap[m.tone]}`}><Icon size={16} /></div>
                  <span className="text-xs text-emerald-400 font-mono flex items-center gap-0.5"><ArrowUpRight size={12} />{m.trend}</span>
                </div>
                <div className="text-2xl font-display font-bold text-white">{m.value}</div>
                <div className="text-xs text-slate-400 mt-1 leading-tight">{m.label}</div>
              </div>
            );
          })}
        </div>

        {/* Charts row */}
        <div className="grid lg:grid-cols-3 gap-3 mb-8">
          <RevenueChart t={t} />
          <WeeklyChart t={t} data={weeklyData} />
        </div>

        {/* Kanban */}
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-white text-xl">{t.liveDash.pipelineTitle}</h3>
          <span className="text-[10px] text-amber-300 font-mono tracking-widest">{t.liveDash.pipelineTag}</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kanban-section">
          {stages.map((s) => (
            <div
              key={s.key}
              className={`glass rounded-xl p-3 min-h-[220px] border-t-2 ${stageColors[s.key]}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(s.key)}
              data-testid={`kanban-column-${s.key}`}
            >
              <div className="flex items-center justify-between mb-3 px-1">
                <div className={`text-xs font-bold tracking-wide ${stageColors[s.key].split(" ")[1]}`}>{s.label}</div>
                <span className="text-xs text-slate-500 font-mono">{board[s.key].length}</span>
              </div>
              <div className="space-y-2">
                {board[s.key].map((card) => (
                  <KanbanCard
                    key={card.id}
                    card={card}
                    info={t.liveDash.cards[card.id] || {}}
                    isDragging={dragging?.cardId === card.id}
                    onDragStart={() => onDragStart(card.id, s.key)}
                    onDragEnd={() => setDragging(null)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-slate-500 text-center italic">
          {t.liveDash.dragNote}
        </p>
      </div>
    </section>
  );
};

const RevenueChart = ({ t }) => (
  <div className="lg:col-span-2 glass rounded-xl p-6">
    <div className="flex items-center justify-between mb-1">
      <div>
        <div className="text-xs text-slate-400 font-mono tracking-wider">{t.liveDash.revLabel}</div>
        <div className="text-2xl font-display font-bold text-white mt-1">€18,420 <span className="text-emerald-400 text-sm font-normal">+34% MoM</span></div>
      </div>
      <div className="text-[10px] text-amber-300 font-mono tracking-widest">DEMO</div>
    </div>
    <div className="h-48 mt-3 -ml-2">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={revenueData}>
          <defs>
            <linearGradient id="revGrad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0" stopColor="#2563EB" stopOpacity="0.6" />
              <stop offset="1" stopColor="#2563EB" stopOpacity="0" />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
          <XAxis dataKey="d" stroke="#64748b" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
          <YAxis stroke="#64748b" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => `€${v}`} />
          <Tooltip contentStyle={{ background: "#0B1228", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} formatter={(v) => `€${v}`} />
          <Area dataKey="rev" stroke="#2563EB" strokeWidth={2} fill="url(#revGrad)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const WeeklyChart = ({ t, data }) => (
  <div className="glass rounded-xl p-6">
    <div className="text-xs text-slate-400 font-mono tracking-wider mb-1">{t.liveDash.weeklyLabel}</div>
    <div className="text-2xl font-display font-bold text-white">{t.liveDash.bookedCount}</div>
    <div className="h-48 mt-3 -ml-2">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} barCategoryGap="20%">
          <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
          <XAxis dataKey="day" stroke="#64748b" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
          <YAxis stroke="#64748b" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={{ background: "#0B1228", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
          <Bar dataKey="recovered" fill="#00F0FF" radius={[4, 4, 0, 0]} />
          <Bar dataKey="booked" fill="#2563EB" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const KanbanCard = ({ card, info, isDragging, onDragStart, onDragEnd }) => (
  <div
    draggable
    onDragStart={onDragStart}
    onDragEnd={onDragEnd}
    className={`kanban-card glass-strong rounded-lg p-3 hover:-translate-y-0.5 hover:border-white/20 ${isDragging ? "dragging" : ""}`}
    data-testid={`kanban-card-${card.id}`}
  >
    <div className="flex items-start justify-between gap-2 mb-1.5">
      <div className="text-sm font-semibold text-white leading-tight">{info.name}</div>
      <GripVertical size={12} className="text-slate-600 mt-0.5 shrink-0" />
    </div>
    <div className="text-xs text-slate-400 leading-snug">{info.job}</div>
    <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/5">
      <span className="text-[10px] text-slate-500 flex items-center gap-1"><Clock size={10} />{info.ago}</span>
      {card.value > 0 && (
        <span className="text-xs font-mono font-semibold text-emerald-300 flex items-center gap-0.5"><Euro size={10} />{card.value}</span>
      )}
    </div>
  </div>
);
